import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Chip from '../../../@vuexy/chips/ChipComponent';
import {
  isOrderEnabled,
  getLocalizedDescription,
  getLocalizedName,
  isAdminMode as _isAdminMode,
  getItemDataOrAdditionPrice,
  getItemDataPriceWithPreSelections,
  getAvailableItemAdditionGroups,
  getEffectiveRequestedAt,
  getOrderConfig,
} from '../../../../utility/util/util.service';
import { ORDER_TYPE_PICKUP } from '../../../../utility/constants';

import LazyImage from '../../lazyload/lazy-image/lazy-image.component';
import classnames from 'classnames';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { connect } from 'react-redux';
import {
  setEditItemModalData,
  setIsEditItemModalOpen,
  setIsItemInfoModalOpen,
  setIsSelectAdditionsModalOpen,
  setItemInfoModalData,
  setSelectAdditionsModalData,
} from '../../../../redux/modal/modal.actions';
import { addCartItem } from '../../../../redux/store/store.actions';
import { Button } from 'reactstrap';
import { SocketContext } from '../../../../context/socket.context';
import { createGuest } from '../../../../utility/sb-api/sb-api.service';
import { fetchOnAuthenticated } from '../../../../redux/me/me.actions';
import xss from 'xss';
import TagManager from 'react-gtm-module/dist/TagManager';
import CategoryBreadCrumbs from '../../helpers/CategoryBreadCrumbs';
import { useLocation } from 'react-router';
import { ReactSVG } from 'react-svg';

const ItemDataInfoModal = ({
  isOpen,
  itemInfoModalData,
  cart,
  me,
  store,
  table,
  kiosk,
  posIntegration,
  checkout,

  addCartItem,
  setSelectAdditionsModalData,
  setIsSelectAdditionsModalOpen,
  setIsItemInfoModalOpen,
  setIsEditItemModalOpen,
  setEditItemModalData,
  fetchOnAuthenticated,
  dailyLimits,
}) => {
  const { itemData, categories } = itemInfoModalData || {};

  // Stock limit check
  const limitInfo = dailyLimits?.find((dl) => dl.itemDataId === itemData?.id);
  const remaining = limitInfo != null ? limitInfo.remaining : null;
  const cartQtyForItem = cart
    ?.filter((ci) => ci.itemData?.id === itemData?.id)
    ?.reduce((s, ci) => s + (ci.qty || 0), 0) || 0;
  const isStockExhausted = remaining != null && cartQtyForItem >= remaining;
  const isAdminMode = _isAdminMode(me, store?.slug);
  const intl = useIntl();
  const socketContext = useContext(SocketContext);
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (itemData) {
      const handleCheck = () => {
        const now = new Date();
        const isAvailable = itemData?.availableAt?.some((slot) => {
          return now >= new Date(slot.startAt) && now <= new Date(slot.endAt);
        });
        setShouldRender(isAvailable);
      };

      if (itemData?.availableAt?.length > 0) {
        handleCheck();
        const intervalId = setInterval(handleCheck, 5000);

        return () => clearInterval(intervalId);
      } else {
        setShouldRender(true);
      }
    }
  }, [itemData, isAdminMode, shouldRender]);

  useEffect(() => {
    if (isOpen && itemData) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'ViewItemData',
          customerProfileId: me?.requester?.customerProfile?.id,
          itemDataId: itemData?.id,
          itemName: getLocalizedName(itemData, intl?.locale),
          itemPrice: intl.formatNumber(itemData.price, {
            format: 'priceCurrency',
          }),
        },
      });
    }
  }, [isOpen, itemData]);

  const preferenceChips = itemData?.preferences?.map(
    (preference, index, all) => (
      <Chip
        key={preference.id}
        className={classnames('mb-50', { 'mr-50': index < all.length - 1 })}
        color="primary"
        avatarColor="white"
        avatarIcon={
          <img
            src={`/assets/icons/preferences/${preference.iconName}`}
            alt={getLocalizedName(preference, intl.locale)}
          />
        }
        text={getLocalizedName(preference, intl.locale)}
      />
    ),
  );

  const ingredientsList = itemData?.ingredients
    ?.map((ingredient) => getLocalizedName(ingredient, intl.locale))
    .join(', ');

  const allergenChips = itemData?.allergens?.map((allergen, index, all) => (
    <div key={allergen.id} className="custom-chip-wrapper">
      <Chip
        className={classnames('mb-50', { 'mr-50': index < all.length - 1 })}
        avatarIcon={
          <ReactSVG
            className="allergen-icon"
            style={{
              width: '24px',
              height: '24px',
            }}
            src={`/assets/icons/allergens/${allergen.iconName}`}
            beforeInjection={(svg) => {
              svg.removeAttribute('width');
              svg.removeAttribute('height');
            }}
          />
        }
        text={getLocalizedName(allergen, intl.locale)}
      />
    </div>
  ));

  const additiveChips = itemData?.additives?.map((additive, index, all) => (
    <div key={additive.id} className="custom-chip-wrapper">
      <Chip
        className={classnames('mb-50', { 'mr-50': index < all.length - 1 })}
        avatarIcon={
          <ReactSVG
            className="allergen-icon"
            style={{
              width: '24px',
              height: '24px',
            }}
            src={`/assets/icons/additives/${additive.iconName}`}
            beforeInjection={(svg) => {
              svg.removeAttribute('width');
              svg.removeAttribute('height');
            }}
          />
        }
        text={getLocalizedName(additive, intl.locale)}
      />
    </div>
  ));

  const availableAdditionsChips = itemData?.additions?.map(
    (addition, index, all) => (
      <div
        key={addition.id}
        className={classnames('chip chip-primary', {
          'mr-50': index < all.length - 1,
        })}
      >
        <div className="chip-body justify-content-center">
          <div className="chip-text">
            {getLocalizedName(addition, intl?.locale)}
          </div>
        </div>
      </div>
    ),
  );

  const description = getLocalizedDescription(itemData, intl.locale);
  // Use orderType for price calculation, fallback to PICKUP for takeaway prices when no orderType is set
  const priceOrderType = checkout?.orderType || ORDER_TYPE_PICKUP;
  const price = useIntl().formatNumber(
    store?.selfServiceOrderConfig?.showCombinationPrice ||
      store?.pickupOrderConfig?.showCombinationPrice
      ? getItemDataPriceWithPreSelections(
          priceOrderType,
          itemData,
          null,
          getEffectiveRequestedAt(checkout),
        )
      : getItemDataOrAdditionPrice(priceOrderType, itemData),
    {
      format: 'priceCurrency',
    },
  );
  const closeModal = () => setIsItemInfoModalOpen(false);

  const customXss = new xss.FilterXSS({
    whiteList: {
      p: [],
      b: [],
      br: [],
      em: [],
      u: [],
      i: [],
      strong: [],
      ul: [],
      li: [],
    },
  });

  const SanitizedHTML = ({ htmlContent }) => {
    const sanitizedHtml = customXss.process(htmlContent);

    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  };

  return (
    <ModalSheet
      isOpen={isOpen}
      closeModal={closeModal}
      title={getLocalizedName(itemData, intl.locale)}
      footer={
        <div className="align-content-center">
          {!isAdminMode &&
            !itemData?.hiddenCategoryOnly &&
            isOrderEnabled(store, itemData, cart, checkout, table, kiosk) && (
              <Button
                color="primary"
                className="w-100 mt-2"
                disabled={isStockExhausted}
                onClick={() => {
                  if (isStockExhausted) return;
                  (!me
                    ? createGuest(
                        { locale: intl?.locale.split('-')[0] },
                        socketContext?.setToken,
                      ).then(() => fetchOnAuthenticated())
                    : Promise.resolve()
                  ).then(() => {
                    TagManager.dataLayer({
                      dataLayer: {
                        event: 'AddToCart',
                        customerProfileId: me?.requester?.customerProfile?.id,
                        itemDataId: itemData?.id,
                        itemName: getLocalizedName(itemData, intl?.locale),
                        itemPrice: intl.formatNumber(itemData?.price, {
                          format: 'priceCurrency',
                        }),
                      },
                    });

                    // need to decide whether to open the select-additions modal or not.
                    const itemAdditionGroups = getAvailableItemAdditionGroups(
                      itemData?.additionGroups,
                      priceOrderType,
                      getOrderConfig(store, checkout?.orderType)?.posIntegration,
                      getEffectiveRequestedAt(checkout),
                    );

                    if (itemAdditionGroups.length > 0) {
                      setSelectAdditionsModalData({
                        itemData,
                        itemAdditionGroups,
                      });
                      setIsSelectAdditionsModalOpen(true);
                    } else {
                      closeModal();
                      addCartItem(cart, itemData, [], 1, priceOrderType);
                    }
                  });
                }}
              >
                {isStockExhausted ? (
                  <FormattedMessage id="soldOut" />
                ) : (
                  <FormattedMessage
                    id="addToCartWithPriceLabel"
                    values={{ price: price }}
                  />
                )}
              </Button>
            )}

          {isAdminMode && (
            <Button
              color="primary"
              className="w-100 mt-2"
              onClick={() => {
                setEditItemModalData({
                  isVariation: true,
                  itemData: { ...itemData, editVariation: true },
                });
                setIsEditItemModalOpen(true);
              }}
            >
              <FormattedMessage id="editItemTitle" />
            </Button>
          )}
        </div>
      }
    >
      <div className="px-1">
        {isAdminMode && (
          <CategoryBreadCrumbs
            categories={categories}
            isStoreOwnerMenu={isAdminMode}
            slug={store?.slug}
          />
        )}
        {itemData?.image && (
          <LazyImage
            image={itemData?.image}
            variant="menuEntryDoubleHeight"
            className="img-cover-center w-100 card-img mt-0 mb-1"
            placeholderContainerClassNames="h-100 mh-18 w-100 line-height-1 d-block mb-1"
            placeholderClassNames="mh-18"
          />
        )}

        <span className="font-weight-bolder font-medium-2 text-dark">
          {getLocalizedName(itemData, intl.locale)}
        </span>

        {description && <SanitizedHTML htmlContent={description} />}
        <div className="d-flex align-items-center justify-content-between">
          {/*<div className="d-flex align-items-center">
            <span className="font-medium-1 text-bold-500">
              {item.likesCount} Likes
            </span>
          </div>*/}
          <span className="font-medium-1 text-bold-500">
            {itemData?.kcal > 0 && (
              <FormattedMessage
                id="kcal"
                values={{ value: itemData?.kcal || 0 }}
              ></FormattedMessage>
            )}
          </span>
          <span className="font-medium-1 text-bold-500">
            {itemData?.price === 0
              ? intl.formatMessage({ id: 'priceFree' }).toUpperCase()
              : price}
          </span>
        </div>

        {availableAdditionsChips?.length > 0 && (
          <div>
            <hr className="my-50" />
            <span className="font-weight-bold text-dark">
              <FormattedMessage id="availableAdditionsTitle" />
            </span>
            <div className="mt-50">{availableAdditionsChips}</div>
          </div>
        )}

        {preferenceChips?.length > 0 && (
          <div>
            <hr className="my-50" />
            <span className="font-weight-bold text-dark">
              <FormattedMessage id="preferencesTitle" />
            </span>
            <div className="mt-50">{preferenceChips}</div>
          </div>
        )}

        {allergenChips?.length > 0 && (
          <div>
            <hr className="my-50" />
            <span className="font-weight-bold text-dark">
              <FormattedMessage id="allergensTitle" />
            </span>
            <div className="mt-50">{allergenChips}</div>
          </div>
        )}

        {additiveChips?.length > 0 && (
          <div>
            <hr className="my-50" />
            <span className="font-weight-bold text-dark">
              <FormattedMessage id="foodAdditives" />
            </span>
            <div className="mt-50">{additiveChips}</div>
          </div>
        )}

        {/* {additiveChips?.length > 0 && (
          <div>
            <hr className="my-50" />
            <span className="font-weight-bold text-dark">
              <FormattedMessage id="additivesTitle" />
            </span>
            <div className="mt-50">{additiveChips}</div>
          </div>
        )} */}
      </div>
    </ModalSheet>
  );
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isItemInfoModalOpen,
  itemInfoModalData: state.modals.itemInfoModalData,
  cart: state.store.cart,
  store: state.store.store,
  me: state.me.me,
  table: state.store.table,
  kiosk: state.store.kiosk,
  checkout: state.store.checkout,
  dailyLimits: state.store.dailyLimits,
});

export default connect(mapStateToProps, {
  setItemInfoModalData,
  setIsItemInfoModalOpen,
  addCartItem,
  setSelectAdditionsModalData,
  setIsSelectAdditionsModalOpen,
  setIsEditItemModalOpen,
  setEditItemModalData,
  fetchOnAuthenticated,
})(ItemDataInfoModal);
